import React, { useState, useEffect, useMemo } from 'react';
import {
    Search,
    Add,
    Edit,
    Delete,
    Person,
    Phone,
    Email,
    Close,
    WhatsApp,
    Sms,
    Call

} from '@mui/icons-material';
import { useSharedData } from "../../contexts/SharedDataContext";
import { fetchCustomers, fetchClientTypes, createCustomer, updateCustomer, deleteCustomer, clientTypeBracket } from "../../services/customerService";
import { fetchClientInsights, groupClientsByPhone } from "../../utils/clientInsights";

// Modal pour ajouter/modifier un client
const ClientModal = ({ show, onClose, onSave, client, isEdit = false, selectedOrganization, organizations }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        zip: '243',
        address: '',
        company_id: selectedOrganization?.id || '',
        client_type: 'PP',
        nif: '',
        docReference: '',
        showInfoOnInvoice: true
    });
    const [selectedOrgId, setSelectedOrgId] = useState(selectedOrganization?.id || '');
    const [submitting, setSubmitting] = useState(false);

    // Initialiser le formulaire avec les données du client en mode édition (comme sur mobile)
    useEffect(() => {
        if (isEdit && client) {
            const orgId = client.company_id || selectedOrganization?.id || '';
            setFormData({
                name: client.name || '',
                email: client.email || '',
                phoneNumber: client.phoneNumber || '',
                zip: client.zip || '243',
                address: client.address || '',
                company_id: orgId,
                client_type: client.client_type || client.clientType || 'PP',
                nif: client.nif || '',
                docReference: client.docReference || '',
                showInfoOnInvoice: client.showInfoOnInvoice !== false
            });
            setSelectedOrgId(orgId);
        } else {
            const defaultOrgId = selectedOrganization?.id || (organizations && organizations.length > 0 ? organizations[0].id : '');
            setFormData({
                name: '',
                email: '',
                phoneNumber: '',
                zip: '243',
                address: '',
                company_id: defaultOrgId,
                client_type: 'PP',
                nif: '',
                docReference: '',
                showInfoOnInvoice: true
            });
            setSelectedOrgId(defaultOrgId);
        }
    }, [client, isEdit, show, selectedOrganization, organizations]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.phoneNumber) {
            alert('Le téléphone est requis');
            return;
        }
        if (['PM', 'PC', 'PL', 'AO'].includes(formData.client_type) && !formData.name) {
            alert(formData.client_type === 'PM' ? 'La dénomination est requise' : 'Le nom est requis');
            return;
        }
        if (['PM', 'PC', 'PL'].includes(formData.client_type) && !formData.nif) {
            alert('Le NIF est requis pour ce type de client');
            return;
        }
        if (formData.client_type === 'AO' && !formData.docReference) {
            alert('La référence document est requise pour AO');
            return;
        }

        if (!selectedOrgId) {
            alert('Veuillez sélectionner une organisation');
            return;
        }

        try {
            setSubmitting(true);
            // ClientRqsDTO: zip, phoneNumber, email, name, nif?, docReference?, client_type, showInfoOnInvoice?, address, company_id
            const customerData = {
                zip: formData.zip || '243',
                address: formData.address || '',
                name: formData.name,
                phoneNumber: formData.phoneNumber,
                email: formData.email || '',
                company_id: selectedOrgId,
                client_type: formData.client_type || 'PP',
                nif: formData.nif || null,
                docReference: formData.docReference || null,
                showInfoOnInvoice: formData.showInfoOnInvoice !== false
            };
            
            await onSave(customerData);
            onClose();
        } catch (error) {
            console.error('[ClientModal] Erreur lors de l\'enregistrement:', error);
            alert(error.message || 'Erreur lors de l\'enregistrement du client');
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="flex items-center justify-between p-6 border-b border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900">
                        {isEdit ? 'Modifier le Client' : 'Nouveau Client'}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                        <Close className="text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Type de client *</label>
                        <select
                            value={formData.client_type || 'PP'}
                            onChange={(e) => setFormData(prev => ({ ...prev, client_type: e.target.value }))}
                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-white"
                        >
                            <option value="PP">Personne Physique [PP]</option>
                            <option value="PM">Personne Morale [PM]</option>
                            <option value="PC">Personne Physique Commerçante [PC]</option>
                            <option value="PL">Profession Libérale [PL]</option>
                            <option value="AO">Ambassades et Organisations [AO]</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            {formData.client_type === 'PM' ? 'Dénomination *' : 'Nom Complet *'}
                        </label>
                        <input
                            type="text"
                            required={['PM', 'PC', 'PL', 'AO'].includes(formData.client_type)}
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                            placeholder={formData.client_type === 'PM' ? 'Dénomination' : 'Nom du client'}
                        />
                    </div>

                    {['PM', 'PC', 'PL'].includes(formData.client_type) && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">NIF *</label>
                            <input
                                type="text"
                                required
                                value={formData.nif}
                                onChange={(e) => setFormData(prev => ({ ...prev, nif: e.target.value }))}
                                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                                placeholder="Numéro d'Identification Fiscale"
                                maxLength={13}
                            />
                        </div>
                    )}
                    {formData.client_type === 'AO' && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Référence document *</label>
                            <input
                                type="text"
                                required
                                value={formData.docReference}
                                onChange={(e) => setFormData(prev => ({ ...prev, docReference: e.target.value }))}
                                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                                placeholder="Référence du document d'exonération"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Email <span className="text-slate-500 font-normal">(Optionnel)</span>
                        </label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                            placeholder="email@exemple.com"
                        />
                    </div>

                    {/* Zip et Téléphone - Layout comme sur mobile (15% et 84%) */}
                    <div className="flex space-x-2">
                        <div className="w-[15%]">
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Zip
                            </label>
                            <input
                                type="text"
                                value={formData.zip}
                                onChange={(e) => setFormData(prev => ({...prev, zip: e.target.value}))}
                                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                                placeholder="243"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Téléphone *
                            </label>
                            <input
                                type="tel"
                                required
                                value={formData.phoneNumber}
                                onChange={(e) => setFormData(prev => ({...prev, phoneNumber: e.target.value}))}
                                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                                placeholder="970000000"
                            />
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Adresse <span className="text-slate-500 font-normal">(Optionnel)</span>
                        </label>
                        <textarea
                            value={formData.address}
                            onChange={(e) => setFormData(prev => ({...prev, address: e.target.value}))}
                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                            placeholder="01, Av. Kasangulu, C/ Lubumbashi, Q. Gambela 2, Lubumbashi."
                            rows="2"
                        />
                    </div>
                    <div className="flex space-x-3 pt-4">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2.5 rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="px-6 py-2.5 bg-slate-500 text-white rounded-xl font-medium hover:bg-slate-600 transition-all duration-300 disabled:opacity-50"
                        >
                            Annuler
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Modal de confirmation de suppression
const DeleteConfirmModal = ({ show, onClose, onConfirm, client }) => {
    if (!show || !client) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="flex items-center justify-between p-6 border-b border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900">Confirmer la suppression</h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                        <Close className="text-slate-500" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Delete className="text-rose-500 text-2xl" />
                        </div>
                        <h4 className="text-lg font-semibold text-slate-900 mb-2">
                            Supprimer {client.name} ?
                        </h4>
                        <p className="text-slate-600">
                            Cette action est irréversible. Toutes les données du client seront perdues.
                        </p>
                    </div>

                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-2.5 bg-slate-500 text-white rounded-xl font-medium hover:bg-slate-600 transition-all duration-300"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={() => onConfirm(client.id)}
                            className="flex-1 px-6 py-2.5 bg-gradient-to-r from-rose-500 to-red-500 text-white rounded-xl font-medium hover:from-rose-600 hover:to-red-600 transition-all duration-300"
                        >
                            Supprimer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Modal de détails du client
const formatMoney = (amount, currency) =>
    `${Number(amount || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`;

const formatDay = (date) =>
    date ? date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const initials = (name) =>
    String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('') || '?';

const displayPhone = (c) => (c.phoneNumber ? `+${c.zip || '243'} ${c.phoneNumber}` : '—');

/** Liens de contact réels (appel, SMS, WhatsApp, e-mail) à partir du numéro et de l'e-mail du client. */
const contactLinks = (c) => {
    const digits = `${c.zip || '243'}${c.phoneNumber || ''}`.replace(/\D/g, '');
    return {
        tel: c.phoneNumber ? `tel:+${digits}` : null,
        sms: c.phoneNumber ? `sms:+${digits}` : null,
        whatsapp: c.phoneNumber ? `https://wa.me/${digits}` : null,
        mail: c.email ? `mailto:${c.email}` : null,
    };
};

const Avatar = ({ client, size = 'w-10 h-10 text-sm' }) => (
    <div className={`${size} rounded-xl flex items-center justify-center font-bold shrink-0 ${
        client.anonymous ? 'bg-slate-100 text-slate-500' : 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'
    }`}>
        {client.anonymous ? <Phone fontSize="small" /> : initials(client.name)}
    </div>
);

const ClientDetailsModal = ({ show, onClose, client, onEdit, onDelete, onMergeDuplicates, merging, canDelete = true, renderClientActions = null }) => {
    if (!show || !client) return null;
    const links = contactLinks(client);
    const { activity } = client;
    const ContactButton = ({ href, icon: Icon, label, tone }) => (
        <a
            href={href || undefined}
            target={href?.startsWith('http') ? '_blank' : undefined}
            rel="noopener noreferrer"
            aria-disabled={!href}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                href ? `border-slate-200 bg-white hover:bg-slate-50 ${tone}` : 'border-slate-100 bg-slate-50 text-slate-300 pointer-events-none'
            }`}
        >
            <Icon fontSize="small" /> {label}
        </a>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
            <div className="w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-3xl bg-slate-50 shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 px-6 pt-6 pb-5 text-white">
                    <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-xl hover:bg-white/10" aria-label="Fermer">
                        <Close />
                    </button>
                    <div className="flex items-center gap-4">
                        <Avatar client={client} size="w-14 h-14 text-lg" />
                        <div className="min-w-0">
                            <h3 className="text-xl font-bold truncate">{client.anonymous ? 'Client sans nom' : client.name}</h3>
                            <p className="text-sm text-slate-300">
                                {displayPhone(client)} · {clientTypeBracket(client)}
                            </p>
                        </div>
                    </div>
                    {client.duplicates.length > 0 && (
                        <p className="mt-4 text-xs text-amber-200 bg-white/10 rounded-lg px-3 py-2">
                            {client.duplicates.length} autre{client.duplicates.length > 1 ? 's' : ''} fiche{client.duplicates.length > 1 ? 's' : ''} avec
                            ce numéro (créées automatiquement lors de paiements) regroupée{client.duplicates.length > 1 ? 's' : ''} ici.
                            <button type="button" onClick={() => onMergeDuplicates(client)} disabled={merging}
                                className="ml-2 underline font-semibold text-white hover:text-amber-100 disabled:opacity-60">
                                {merging ? 'Fusion en cours…' : 'Fusionner définitivement'}
                            </button>
                        </p>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-2xl bg-white border border-slate-200 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paiements</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{activity.count}</p>
                        </div>
                        <div className="rounded-2xl bg-white border border-slate-200 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total payé</p>
                            <p className="mt-1 text-sm font-bold text-slate-900">{formatMoney(activity.totals.CDF, 'CDF')}</p>
                            {activity.totals.USD > 0 && <p className="text-sm font-bold text-slate-900">{formatMoney(activity.totals.USD, 'USD')}</p>}
                        </div>
                        <div className="rounded-2xl bg-white border border-slate-200 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dernier paiement</p>
                            <p className="mt-1 text-sm font-bold text-slate-900">{formatDay(activity.lastAt)}</p>
                        </div>
                    </div>

                    <div className="rounded-2xl bg-white border border-slate-200 p-4 space-y-2 text-sm">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Coordonnées</p>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Téléphone</span><span className="font-semibold">{displayPhone(client)}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">E-mail</span><span className="font-semibold break-all">{client.email || '—'}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-slate-500">Adresse</span><span className="font-semibold text-right">{client.address || '—'}</span></div>
                        {client.nif && <div className="flex justify-between gap-4"><span className="text-slate-500">NIF</span><span className="font-semibold">{client.nif}</span></div>}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <ContactButton href={links.tel} icon={Call} label="Appeler" tone="text-slate-700" />
                        <ContactButton href={links.whatsapp} icon={WhatsApp} label="WhatsApp" tone="text-emerald-600" />
                        <ContactButton href={links.sms} icon={Sms} label="SMS" tone="text-blue-600" />
                        <ContactButton href={links.mail} icon={Email} label="E-mail" tone="text-amber-600" />
                    </div>

                    <div className="rounded-2xl bg-white border border-slate-200 p-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Derniers paiements</p>
                        {activity.payments.length === 0 ? (
                            <p className="text-sm text-slate-500 py-2">Aucun paiement réussi enregistré pour ce client.</p>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {activity.payments.slice(0, 5).map((p) => (
                                    <li key={p.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                                        <div className="min-w-0">
                                            <p className="font-mono font-semibold text-slate-800 truncate">{p.reference}</p>
                                            <p className="text-xs text-slate-500 truncate">{p.label || 'Paiement'} · {formatDay(p.at)}</p>
                                        </div>
                                        <span className="font-semibold text-slate-900 shrink-0">{formatMoney(p.amount, p.currency)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="border-t border-slate-200 bg-white p-4 flex flex-wrap gap-3">
                    <button type="button" onClick={() => { onClose(); onEdit(client); }}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 font-semibold text-white hover:from-amber-600 hover:to-orange-600">
                        <Edit fontSize="small" /> {client.anonymous ? 'Compléter la fiche' : 'Modifier'}
                    </button>
                    {renderClientActions && renderClientActions(client, onClose)}
                    {canDelete && (
                        <button type="button" onClick={() => { onClose(); onDelete(client); }}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-200 px-4 py-3 font-medium text-red-600 hover:bg-red-50">
                            <Delete fontSize="small" /> Supprimer
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const SORTS = [
    { id: 'recent', label: 'Dernier paiement' },
    { id: 'top', label: 'Plus gros payeurs' },
    { id: 'name', label: 'Nom (A → Z)' },
];

/**
 * Annuaire des clients (admin et caissier) : un client par numéro, activité réelle,
 * filtres, fiche détaillée. `emitterId` limite l'activité aux paiements de ce caissier.
 */
const ClientsDirectory = ({
    selectedOrganization,
    organizations,
    emitterId = null,
    currency = 'CDF',
    canDelete = true,
    renderClientActions = null,
    subtitle = 'Vos clients et leurs paiements réels, un client par numéro de téléphone.',
}) => {
    const { newCustomer, setNewCustomer } = useSharedData();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [identityFilter, setIdentityFilter] = useState('all');
    const [sort, setSort] = useState('recent');
    const [clientTypes, setClientTypes] = useState([]);
    const [rawClients, setRawClients] = useState([]);
    const [insights, setInsights] = useState(new Map());
    const [loading, setLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [showClientModal, setShowClientModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // Recherche serveur (nom, téléphone, e-mail) après une courte pause de frappe
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => {
        fetchClientTypes({ pge: 1, lmt: 50, slg: '' })
            .then((res) => setClientTypes(Array.isArray(res?.contents) ? res.contents : []))
            .catch(() => setClientTypes([]));
    }, []);

    useEffect(() => {
        if (newCustomer) {
            setNewCustomer(null);
            setReloadKey((k) => k + 1);
        }
    }, [newCustomer, setNewCustomer]);

    useEffect(() => {
        if (!selectedOrganization?.id) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([
            fetchCustomers({ org: selectedOrganization.id, pge: 1, lmt: 1000, slg: debouncedSearch }),
            fetchClientInsights(selectedOrganization.id, emitterId).catch(() => new Map()),
        ])
            .then(([response, activity]) => {
                if (cancelled) return;
                setRawClients((response.contents || []).map((c) => ({
                    id: c.id,
                    name: c.name || '',
                    email: c.email || '',
                    phoneNumber: c.phoneNumber || '',
                    zip: c.zip || '243',
                    address: c.address || '',
                    client_type: c.client_type || 'PP',
                    client_type_id: c.client_type_id || null,
                    clientTypeDescription: c.clientTypeDescription || null,
                    nif: c.nif || '',
                    docReference: c.docReference || '',
                    joinDate: c.createAt || c.create_at ? new Date(c.createAt || c.create_at) : null,
                    company_id: c.company_id || selectedOrganization.id,
                })));
                setInsights(activity);
            })
            .catch((error) => {
                console.error('Error loading clients:', error);
                if (!cancelled) setRawClients([]);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [selectedOrganization?.id, emitterId, debouncedSearch, reloadKey]);

    const grouped = useMemo(() => groupClientsByPhone(rawClients, insights), [rawClients, insights]);

    const typeCounts = useMemo(() => {
        const counts = {};
        grouped.forEach((c) => { counts[c.client_type] = (counts[c.client_type] || 0) + 1; });
        return counts;
    }, [grouped]);

    const stats = useMemo(() => {
        const since = Date.now() - 30 * 24 * 60 * 60 * 1000;
        return {
            total: grouped.length,
            identified: grouped.filter((c) => !c.anonymous).length,
            anonymous: grouped.filter((c) => c.anonymous).length,
            active: grouped.filter((c) => c.activity.lastAt && c.activity.lastAt.getTime() >= since).length,
            duplicates: grouped.reduce((n, c) => n + c.duplicates.length, 0),
        };
    }, [grouped]);

    const visibleClients = useMemo(() => {
        const list = grouped.filter((c) =>
            (filterType === 'all' || c.client_type === filterType)
            && (identityFilter === 'all' || (identityFilter === 'anonymous' ? c.anonymous : !c.anonymous)));
        const byName = (a, b) => (a.anonymous ? '~' : a.name).localeCompare(b.anonymous ? '~' : b.name, 'fr');
        return list.sort((a, b) => {
            if (sort === 'name') return byName(a, b);
            if (sort === 'top') return (b.activity.totals[currency] || 0) - (a.activity.totals[currency] || 0) || byName(a, b);
            return (b.activity.lastAt?.getTime() || 0) - (a.activity.lastAt?.getTime() || 0) || byName(a, b);
        });
    }, [grouped, filterType, identityFilter, sort, currency]);

    const handleAddClient = () => { setSelectedClient(null); setIsEditing(false); setShowClientModal(true); };
    const handleEditClient = (client) => { setSelectedClient(client); setIsEditing(true); setShowClientModal(true); };
    const handleDeleteClient = (client) => { setSelectedClient(client); setShowDeleteModal(true); };
    const handleViewDetails = (client) => { setSelectedClient(client); setShowDetailsModal(true); };

    const handleSaveClient = async (clientData) => {
        try {
            if (isEditing && selectedClient) {
                await updateCustomer(selectedClient.id, clientData);
            } else {
                await createCustomer(clientData);
            }
            setReloadKey((k) => k + 1);
        } catch (error) {
            alert(error?.response?.data?.message || error?.message || 'Une erreur est survenue. Veuillez réessayer.');
            throw error;
        }
    };

    /** Fusionne les fiches en double dans la fiche principale (le serveur rattache leur historique). */
    const mergeDuplicates = async (client) => {
        for (const duplicate of client.duplicates || []) {
            await deleteCustomer(duplicate.id);
        }
    };

    const [merging, setMerging] = useState(false);
    const handleMergeDuplicates = async (client) => {
        setMerging(true);
        try {
            await mergeDuplicates(client);
            setShowDetailsModal(false);
            setReloadKey((k) => k + 1);
            alert(`${client.duplicates.length} fiche(s) en double fusionnée(s) avec ${client.anonymous ? displayPhone(client) : client.name}.`);
        } catch (error) {
            alert(error.message);
            setReloadKey((k) => k + 1);
        } finally {
            setMerging(false);
        }
    };

    const handleConfirmDelete = async (clientId) => {
        try {
            if (selectedClient?.duplicates?.length) {
                await mergeDuplicates(selectedClient);
            }
            const result = await deleteCustomer(clientId);
            setShowDeleteModal(false);
            setSelectedClient(null);
            setReloadKey((k) => k + 1);
            if (result?.merged_into) alert(result.message);
        } catch (error) {
            setShowDeleteModal(false);
            setReloadKey((k) => k + 1);
            alert(error.message || 'Erreur lors de la suppression du client');
        }
    };

    const chip = (active) => `px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
        active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
    }`;

    const StatCard = ({ label, value, hint, tone }) => (
        <div className="rounded-2xl bg-white border border-slate-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
            <p className={`mt-1 text-2xl font-bold ${tone || 'text-slate-900'}`}>{loading ? '…' : value}</p>
            {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
        </div>
    );

    return (
        <>
                    <div className="space-y-5 ">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Clients</h1>
                                <p className="text-sm text-slate-500">{subtitle}</p>
                            </div>
                            <button type="button" onClick={handleAddClient}
                                className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 shadow-sm">
                                <Add fontSize="small" /> Nouveau client
                            </button>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <StatCard label="Clients" value={stats.total}
                                hint={stats.duplicates ? `${stats.duplicates} fiche(s) en double regroupée(s)` : 'Un par numéro'} />
                            <StatCard label="Identifiés" value={stats.identified} hint="Avec un nom" tone="text-emerald-600" />
                            <StatCard label="Sans nom" value={stats.anonymous} hint="Créés lors d'un paiement" tone="text-slate-500" />
                            <StatCard label="Actifs (30 j)" value={stats.active} hint="Ont payé ce mois-ci" tone="text-amber-600" />
                        </div>

                        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-3">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Rechercher par nom, téléphone ou e-mail…"
                                        className="pl-10 pr-4 py-2.5 w-full border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                                </div>
                                <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Trier"
                                    className="px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white">
                                    {SORTS.map((s) => <option key={s.id} value={s.id}>Trier : {s.label}</option>)}
                                </select>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button type="button" onClick={() => setIdentityFilter('all')} className={chip(identityFilter === 'all')}>Tous</button>
                                <button type="button" onClick={() => setIdentityFilter('identified')} className={chip(identityFilter === 'identified')}>Identifiés ({stats.identified})</button>
                                <button type="button" onClick={() => setIdentityFilter('anonymous')} className={chip(identityFilter === 'anonymous')}>Sans nom ({stats.anonymous})</button>
                                <span className="w-px h-6 bg-slate-200 mx-1" />
                                <button type="button" onClick={() => setFilterType('all')} className={chip(filterType === 'all')}>Tous types</button>
                                {clientTypes.filter((ct) => typeCounts[ct.type ?? ct.code]).map((ct) => {
                                    const code = ct.type ?? ct.code;
                                    return (
                                        <button type="button" key={ct.id} onClick={() => setFilterType(code)} className={chip(filterType === code)} title={ct.description}>
                                            {code} ({typeCounts[code]})
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            {loading ? (
                                <div className="p-12 text-center text-slate-500">Chargement des clients…</div>
                            ) : visibleClients.length === 0 ? (
                                <div className="p-12 text-center">
                                    <Person className="text-slate-300 mx-auto" sx={{ fontSize: 48 }} />
                                    <p className="mt-2 font-semibold text-slate-700">
                                        {searchTerm || filterType !== 'all' || identityFilter !== 'all' ? 'Aucun client ne correspond' : 'Aucun client pour le moment'}
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[760px]">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                                                <th className="px-5 py-3">Client</th>
                                                <th className="px-5 py-3">Contact</th>
                                                <th className="px-5 py-3 text-right">Paiements</th>
                                                <th className="px-5 py-3 text-right">Total payé</th>
                                                <th className="px-5 py-3">Dernier paiement</th>
                                                <th className="px-5 py-3" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {visibleClients.map((client) => (
                                                <tr key={client.key} onClick={() => handleViewDetails(client)} className="hover:bg-amber-50/40 cursor-pointer transition-colors">
                                                    <td className="px-5 py-3.5">
                                                        <div className="flex items-center gap-3">
                                                            <Avatar client={client} />
                                                            <div className="min-w-0">
                                                                <p className={`font-semibold truncate ${client.anonymous ? 'text-slate-500 italic' : 'text-slate-900'}`}>
                                                                    {client.anonymous ? 'Client sans nom' : client.name}
                                                                </p>
                                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                                    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{client.client_type}</span>
                                                                    {client.duplicates.length > 0 && (
                                                                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" title="Fiches regroupées sur ce numéro">
                                                                            +{client.duplicates.length} fiche{client.duplicates.length > 1 ? 's' : ''}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-3.5 text-sm">
                                                        <p className="font-medium text-slate-800">{displayPhone(client)}</p>
                                                        <p className="text-slate-500 truncate max-w-[220px]">{client.email || '—'}</p>
                                                    </td>
                                                    <td className="px-5 py-3.5 text-right font-semibold text-slate-900 tabular-nums">{client.activity.count}</td>
                                                    <td className="px-5 py-3.5 text-right text-sm tabular-nums">
                                                        <p className="font-semibold text-slate-900">{formatMoney(client.activity.totals.CDF, 'CDF')}</p>
                                                        {client.activity.totals.USD > 0 && <p className="text-slate-600">{formatMoney(client.activity.totals.USD, 'USD')}</p>}
                                                    </td>
                                                    <td className="px-5 py-3.5 text-sm text-slate-600">{formatDay(client.activity.lastAt)}</td>
                                                    <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                                                        <button type="button" onClick={() => handleEditClient(client)} className="p-2 rounded-lg text-slate-500 hover:bg-amber-100 hover:text-amber-700" title="Modifier">
                                                            <Edit fontSize="small" />
                                                        </button>
                                                        {canDelete && (
                                                            <button type="button" onClick={() => handleDeleteClient(client)} className="p-2 rounded-lg text-slate-500 hover:bg-red-100 hover:text-red-600" title="Supprimer">
                                                                <Delete fontSize="small" />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <ClientModal
                        show={showClientModal}
                        onClose={() => setShowClientModal(false)}
                        onSave={handleSaveClient}
                        client={selectedClient}
                        isEdit={isEditing}
                        selectedOrganization={selectedOrganization}
                        organizations={organizations}
                    />

                    <DeleteConfirmModal
                        show={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={handleConfirmDelete}
                        client={selectedClient}
                    />

                    <ClientDetailsModal
                        show={showDetailsModal}
                        onClose={() => setShowDetailsModal(false)}
                        client={selectedClient}
                        onEdit={handleEditClient}
                        onDelete={handleDeleteClient}
                        onMergeDuplicates={handleMergeDuplicates}
                        merging={merging}
                        canDelete={canDelete}
                        renderClientActions={renderClientActions}
                    />
        </>
    );
};

export { ClientModal };
export default ClientsDirectory;
